// Shared vocabulary for the Usage tab's "Doing what" dimension: the customer
// label and the chart color per purpose, plus the ledger's non-model
// segments. The breakdown cards and the stacked spend chart both read from
// here so a card row and its chart segment always wear the same color.
//
// Color follows the purpose, never its rank: a purpose keeps its hue whatever
// else is on screen. There are six validated hues (--viz-1..6 in index.css)
// and no seventh is ever generated; purposes without a hue render as "Other
// activity". "Other activity" and the non-model ledger charges
// (infrastructure, add-on services, base fee) are one neutral ladder mixed
// from the theme's foreground and background, so it flips with the theme:
// "Other activity" is the most visible step and each overhead step fades
// further toward the surface. Adjacent steps stay >= 0.15 apart in OKLab
// lightness in both themes; keep that spacing if a step changes.

/** Product-voice purpose labels (BOO-198: no "agent"/"AI"). Triage cost is
 * folded into chat ledger-side, so it never appears as its own category. */
export const PURPOSE_LABELS: Record<string, string> = {
  chat: "Replying in chat",
  heartbeats: "Scheduled tasks",
  workflow: "Workflows",
  subagents: "Helpers",
  coding: "Coding",
  // Housekeeping the Boo does on its own: compacting long conversations so
  // it can keep going, and indexing past conversations so it can find them.
  compaction: "Compacting context",
  indexing: "Indexing",
  embeddings: "Embeddings",
};

export function purposeLabel(key: string): string {
  return PURPOSE_LABELS[key] ?? key;
}

/** Fixed hue per purpose. Purposes absent here (indexing, embeddings, any
 * purpose the dashboard doesn't know yet) take OTHER_ACTIVITY.color. */
export const PURPOSE_COLORS: Partial<Record<string, string>> = {
  chat: "var(--viz-1)",
  heartbeats: "var(--viz-2)",
  workflow: "var(--viz-3)",
  subagents: "var(--viz-4)",
  coding: "var(--viz-5)",
  compaction: "var(--viz-6)",
};

/** Whether a purpose has a hue of its own (and so may get its own chart
 * segment instead of folding into "Other activity"). */
export function purposeHasColor(key: string): boolean {
  return PURPOSE_COLORS[key] !== undefined;
}

// A step of the neutral ladder: `pct` of the foreground into the background.
const neutral = (pct: number) =>
  `color-mix(in oklch, var(--foreground) ${pct}%, var(--background))`;

export const OTHER_ACTIVITY = {
  label: "Other activity",
  color: neutral(66),
} as const;

export function purposeColor(key: string): string {
  return PURPOSE_COLORS[key] ?? OTHER_ACTIVITY.color;
}

/** The credit ledger's non-model charges, keyed by their field on the
 * ledger's by-day rows. */
export const LEDGER_SEGMENTS = {
  infrastructure: { label: "Infrastructure", color: neutral(48) },
  services: { label: "Add-on services", color: neutral(30) },
  base: { label: "Base fee", color: neutral(12) },
} as const;

export type LedgerSegmentKey = keyof typeof LEDGER_SEGMENTS;
